import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { AuthScreen } from './AuthScreen'
import { PACK_COLORS } from '@/design-system/tokens'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/Button'
import { CheckIcon } from '@/design-system/icons'

/**
 * /join?token=<invite_jwt>
 *
 * Handles caregiver invite redemption. Renders in ALL auth states — signed_out, signed_in_no_family,
 * signed_in_active — so a click on an invite link works whether Emma has an account yet or not.
 *
 * Flow:
 *   1. Read token from URL. If missing → show error.
 *   2. If not signed in → embed AuthScreen. After sign-in, refreshSession fires, this component
 *      re-renders with a session, proceeds to accept.
 *   3. If signed in → POST invites/accept. On success, refresh session (picks up new family
 *      membership) and navigate to /.
 */
export function JoinScreen() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const nav = useNavigate()
  const status = useAppStore((s) => s.status)
  const refreshSession = useAppStore((s) => s.refreshSession)

  const [phase, setPhase] = useState<'checking' | 'accepting' | 'success' | 'error'>('checking')
  const [message, setMessage] = useState('')
  const acceptedRef = useRef(false)

  useEffect(() => {
    if (!token) {
      setPhase('error')
      setMessage('Invalid invite link — no token in the URL. Ask the person who invited you to share the link again.')
      return
    }
    // Belt-and-suspenders: stash the token in sessionStorage so if Supabase's email-confirm
    // link brings the user back to `/` (instead of `/join?token=xxx` — misconfig, redirect
    // strips, etc.), the app can still pick up the pending invite and continue.
    try {
      sessionStorage.setItem('babyos.pending_invite', token)
    } catch { /* private mode etc — noop */ }

    if (status === 'loading' || status === 'unconfigured') return
    if (status === 'signed_out') {
      // AuthScreen renders below; when session becomes active this effect re-runs and accepts.
      return
    }
    // signed_in_no_family OR active — either way, accept the invite.
    void acceptToken(token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, status])

  async function acceptToken(inviteToken: string) {
    if (acceptedRef.current) return
    acceptedRef.current = true
    setPhase('accepting')
    setMessage('')
    try {
      const { data, error } = await supabase.functions.invoke('invites/accept', {
        body: { invite_token: inviteToken },
      })
      if (error) {
        // functions.invoke wraps HTTP errors; try to read the body if present.
        let detail = error.message
        try {
          if ((error as { context?: { text?: () => Promise<string> } }).context?.text) {
            const body = await (error as { context: { text: () => Promise<string> } }).context.text()
            const parsed = JSON.parse(body)
            detail = parsed.error ?? parsed.detail ?? body
          }
        } catch {
          /* keep original message */
        }
        throw new Error(detail)
      }
      if (!data || typeof data !== 'object' || !('family_id' in data)) {
        throw new Error(`Accept returned unexpected shape: ${JSON.stringify(data)}`)
      }
      setPhase('success')
      try { sessionStorage.removeItem('babyos.pending_invite') } catch { /* noop */ }
      // Refresh session so the new family_members row is picked up and the app switches to Today.
      await refreshSession()
      setTimeout(() => nav('/', { replace: true }), 1500)
    } catch (e) {
      acceptedRef.current = false
      setPhase('error')
      const raw = e instanceof Error ? e.message : String(e)
      setMessage(friendlyMessage(raw))
    }
  }

  // Not signed in → embed AuthScreen. Preserve the join URL so the user comes back here after auth.
  if (status === 'signed_out') {
    return (
      <div className="min-h-svh bg-bg-base">
        <div className="mx-auto max-w-sm px-6 pt-safe pt-12 text-center">
          <Kicker color={PACK_COLORS.early_care.light}>You've been invited</Kicker>
          <div className="mt-3 font-serif italic text-hero text-text-primary">
            Sign in to join the family
          </div>
          <div className="mt-2 text-caption text-text-tertiary">
            Once you're signed in we'll add you to the family automatically.
          </div>
        </div>
        <AuthScreen />
      </div>
    )
  }

  const packColor = PACK_COLORS.early_care.light
  return (
    <div className="min-h-svh flex items-center justify-center bg-bg-base px-6">
      <div className="max-w-sm text-center">
        {phase === 'checking' && (
          <>
            <Kicker>Loading invite</Kicker>
            <div className="mt-3 font-serif italic text-hero text-text-primary">One moment…</div>
          </>
        )}
        {phase === 'accepting' && (
          <>
            <Kicker color={packColor}>Joining the family</Kicker>
            <div className="mt-3 font-serif italic text-hero text-text-primary">Almost there.</div>
          </>
        )}
        {phase === 'success' && (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: packColor + '33', color: packColor }}>
              <CheckIcon size={28} />
            </div>
            <Kicker color={packColor}>Welcome</Kicker>
            <div className="mt-3 font-serif italic text-hero text-text-primary">You're in.</div>
            <div className="mt-2 text-caption text-text-tertiary">Taking you to Today…</div>
          </>
        )}
        {phase === 'error' && (
          <>
            <Kicker color={PACK_COLORS.safety.light}>Invite issue</Kicker>
            <div className="mt-3 font-serif italic text-hero text-text-primary">Can't accept this invite.</div>
            <div className="mt-3 text-body text-text-secondary">{message}</div>
            <div className="mt-6">
              <Button variant="secondary" size="lg" onClick={() => nav('/', { replace: true })} className="w-full">
                Go home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function friendlyMessage(raw: string): string {
  switch (raw) {
    case 'invite_already_redeemed':
      return 'This invite has already been used.'
    case 'invite_revoked':
      return 'This invite was revoked. Ask the family owner to send a new one.'
    case 'invite_expired':
      return 'This invite has expired. Ask the family owner to send a new one.'
    case 'invite_email_mismatch':
      return "This invite is for a different email address. Sign in with the email it was sent to."
    case 'invite_not_found':
      return "We couldn't find this invite. It may have been deleted."
    case 'invalid_or_expired_invite':
      return 'The invite link is invalid or expired.'
    case 'unauthenticated':
    case 'invalid_jwt':
      return 'Sign-in failed. Try signing in again.'
    default:
      return raw
  }
}
